import { cn } from './chunk-FFX3LZW7.js';
import * as React2 from 'react';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { jsx, jsxs } from 'react/jsx-runtime';
import { getTruncation, hexPrefixOf } from '@authe/fe-commons/display';
import { truncationKindOf } from '@authe/fe-commons/chains';
import { ExternalLink as ExternalLink$1 } from 'lucide-react';

var HoverCard = HoverCardPrimitive.Root;
var HoverCardTrigger = HoverCardPrimitive.Trigger;
var HoverCardContent = React2.forwardRef(function HoverCardContent2({ className, align = "center", sideOffset = 4, ...props }, ref) {
  return /* @__PURE__ */ jsx(HoverCardPrimitive.Portal, { children: /* @__PURE__ */ jsx(
    HoverCardPrimitive.Content,
    {
      ref,
      align,
      sideOffset,
      className: cn(
        "z-50 w-64 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out",
        className
      ),
      ...props
    }
  ) });
});
var QUICK_ACTION_CLASS = "quick-action";
var QUICK_ACTIONS_SCOPE = { "data-quick-actions-scope": "" };
var QuickActionsContext = React2.createContext({});
function QuickActionsProvider({
  actions,
  userActions,
  children
}) {
  const parent = React2.useContext(QuickActionsContext);
  const value = React2.useMemo(
    () => ({
      actions: actions ?? parent.actions,
      // Inherited unless a provider states it again: a surface default must not
      // be able to drop the reader's own choice by wrapping itself in a provider.
      userActions: userActions ?? parent.userActions
    }),
    [actions, userActions, parent.actions, parent.userActions]
  );
  return /* @__PURE__ */ jsx(QuickActionsContext.Provider, { value, children });
}
function resolveQuickActions(own, settings) {
  if (settings.userActions && settings.userActions !== "auto") return settings.userActions;
  return own ?? settings.actions ?? "auto";
}
function useQuickActions(own) {
  return resolveQuickActions(own, React2.useContext(QuickActionsContext));
}
function MiddleEllipsis({
  value,
  kind,
  className,
  title,
  monospace = true
}) {
  const { head, tail } = getTruncation(kind === "auto" ? truncationKindOf(value) : kind);
  const prefix = hexPrefixOf(value);
  const base = cn("text-sm", monospace && "font-mono", className);
  if (value.length - prefix.length <= head + tail + 1 || /\s/.test(value.trim())) {
    return /* @__PURE__ */ jsx("span", { className: base, title: title ?? value, children: value });
  }
  const headWidth = prefix.length + head + 1;
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn("authe-middle-ellipsis", base),
      "data-tail": value.slice(-tail),
      style: { maxWidth: `min(100%, calc(${headWidth + tail}ch + 1px))` },
      title: title ?? value,
      children: /* @__PURE__ */ jsx("span", { className: "authe-middle-ellipsis__head", style: { maxWidth: `calc(${headWidth}ch + 1px)` }, children: value })
    }
  );
}
function opensInApp(event) {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}
var NEW_TAB_HINT = "(opens in a new tab)";
var ICON_CLASS = {
  3: "size-3",
  3.5: "size-3.5",
  4: "size-4"
};
function ExternalLink({
  href,
  children,
  className,
  showIcon = true,
  iconSize = 3,
  hideNewTabHint = false,
  ...rest
}) {
  return /* @__PURE__ */ jsxs(
    "a",
    {
      ...rest,
      href,
      target: "_blank",
      rel: "noreferrer noopener",
      className: cn("inline-flex min-w-0 max-w-full items-center gap-1 hover:underline", className),
      children: [
        children,
        showIcon && /* @__PURE__ */ jsx(ExternalLink$1, { className: cn("shrink-0", ICON_CLASS[iconSize]), "aria-hidden": true }),
        !hideNewTabHint && /* @__PURE__ */ jsxs("span", { className: "sr-only", children: [
          " ",
          NEW_TAB_HINT
        ] })
      ]
    }
  );
}
function RichTooltip({
  children,
  title,
  subtitle,
  headerRight,
  rows,
  actions,
  content,
  align = "start",
  side = "top",
  openDelay = 150,
  closeDelay = 80,
  className,
  asChild = true
}) {
  const hasBody = Boolean(title || subtitle || rows && rows.length || content || actions && actions.length);
  return /* @__PURE__ */ jsxs(HoverCard, { openDelay, closeDelay, children: [
    /* @__PURE__ */ jsx(HoverCardTrigger, { asChild, children: asChild ? children : /* @__PURE__ */ jsx("span", { children }) }),
    hasBody && /* @__PURE__ */ jsxs(
      HoverCardContent,
      {
        align,
        side,
        className: cn("w-72 space-y-2 p-3 text-sm", className),
        children: [
          (title || subtitle || headerRight) && /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
            /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
              title && /* @__PURE__ */ jsx("div", { className: "truncate font-medium leading-tight", children: title }),
              subtitle && /* @__PURE__ */ jsx("div", { className: "truncate text-xs text-muted-foreground", children: subtitle })
            ] }),
            headerRight && /* @__PURE__ */ jsx("div", { className: "shrink-0", children: headerRight })
          ] }),
          rows && rows.length > 0 && /* @__PURE__ */ jsx("dl", { className: "space-y-1", children: rows.map((row, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-3", children: [
            /* @__PURE__ */ jsx("dt", { className: "shrink-0 text-xs text-muted-foreground", children: row.label }),
            /* @__PURE__ */ jsx("dd", { className: "min-w-0 truncate text-right text-xs font-medium", children: row.value })
          ] }, i)) }),
          content,
          actions && actions.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1 border-t pt-2", children: actions.map((action) => {
            const Icon = action.icon;
            return /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                onClick: (e) => {
                  e.stopPropagation();
                  action.onClick();
                },
                "aria-label": action.label,
                title: action.label,
                className: "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground",
                children: [
                  Icon && /* @__PURE__ */ jsx(Icon, { className: "size-3.5" }),
                  !action.iconOnly && /* @__PURE__ */ jsx("span", { children: action.label })
                ]
              },
              action.label
            );
          }) })
        ]
      }
    )
  ] });
}

export { ExternalLink, HoverCard, HoverCardContent, HoverCardTrigger, MiddleEllipsis, NEW_TAB_HINT, QUICK_ACTIONS_SCOPE, QUICK_ACTION_CLASS, QuickActionsProvider, RichTooltip, opensInApp, resolveQuickActions, useQuickActions };
//# sourceMappingURL=chunk-H33RBJAU.js.map
//# sourceMappingURL=chunk-H33RBJAU.js.map